/**
 * CoverageQaFeedback - Labeler feedback view for Coverage QA results.
 *
 * This page allows labelers to view feedback from QA reviewers on their
 * completed coverage tasks. It displays the QA outcome (approved/corrected/rejected)
 * in a simple, centered card format.
 *
 * ## QA Result Types
 * - **Approved**: Labeler's work was correct
 * - **Corrected**: QA reviewer fixed the product selection (shows corrected product IDs)
 * - **Rejected**: Task couldn't be evaluated due to image issues
 *
 * @route /jobs/:jobId/coverage-qa-feedback?taskId=:taskId
 */
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useFetchTask } from "@labeling-tools/api/lebron/react-query-hooks";
import {
  LoadingOverlay,
  Card,
  Text,
  Badge,
  Stack,
  Button,
  List,
} from "@mantine/core";
import type { CoverageQaResult, CoverageQaTaskReason } from "@/types";
import { useProfileHome } from "@/hooks/useProfileHome";

/** Human-readable labels for task rejection reasons (subset shown to labelers). */
const TASK_REJECTION_REASON_LABELS: Record<CoverageQaTaskReason, string> = {
  image_quality: "Image Quality",
  obstructed_view: "Obstructed View",
  bad_angle: "Bad Angle",
  unsupported_planogram: "Unsupported Planogram",
};

export const CoverageQaFeedback = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { jobsPath } = useProfileHome();
  const taskId = searchParams.get("taskId");

  // Fetch task data
  const { data: task, isLoading } = useFetchTask(jobId || "", taskId);

  // Extract QA result from task
  const qaResult = task?.result as CoverageQaResult | undefined;

  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  if (!qaResult) {
    return (
      <div className="flex items-center justify-center min-h-screen p-8">
        <Card shadow="md" padding="xl" withBorder className="max-w-md w-full">
          <Stack align="center" gap="lg">
            <Text c="dimmed">No QA result found for this task.</Text>
            <Button fullWidth onClick={() => navigate(jobsPath)}>
              Return Home
            </Button>
          </Stack>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-8">
      <Card shadow="md" padding="xl" withBorder className="max-w-md w-full">
        <Stack align="center" gap="lg">
          {/* Approved Status */}
          {qaResult.status === "approved" && (
            <>
              <Badge color="green" size="xl" radius="md">
                ✓ Approved
              </Badge>
              <Text c="dimmed" ta="center">
                Your coverage task was approved by QA.
              </Text>
            </>
          )}

          {/* Corrected Status */}
          {qaResult.status === "corrected" && (
            <>
              <Badge color="yellow" size="xl" radius="md">
                ⚡ Corrected
              </Badge>
              <Text c="dimmed" ta="center">
                QA made a correction to the product selection.
              </Text>

              <div className="w-full">
                <Text size="sm" fw={500} mb="xs">
                  Corrected Product IDs:
                </Text>
                <List size="sm" className="bg-gray-50 p-3 rounded">
                  {qaResult.corrected_product_ids.map((id, i) => (
                    <List.Item key={i}>{id}</List.Item>
                  ))}
                </List>
              </div>

              {qaResult.corrected_planogram_id && (
                <div className="w-full">
                  <Text size="sm" fw={500} mb="xs">
                    Corrected Planogram:
                  </Text>
                  <Text size="sm" className="bg-gray-50 p-3 rounded">
                    {qaResult.corrected_planogram_id}
                  </Text>
                </div>
              )}
            </>
          )}

          {/* Rejected Status */}
          {qaResult.status === "rejected" && (
            <>
              <Badge color="red" size="xl" radius="md">
                ✗ Rejected
              </Badge>
              <Text c="dimmed" ta="center">
                Your task was marked as unevaluable due to image issues.
              </Text>
              <div className="w-full">
                <Text size="sm" fw={500} mb="xs">
                  Reason:
                </Text>
                <Text size="sm" className="bg-gray-50 p-3 rounded">
                  {TASK_REJECTION_REASON_LABELS[
                    qaResult.reason as CoverageQaTaskReason
                  ] || qaResult.reason}
                </Text>
              </div>
            </>
          )}

          {/* Acknowledge Button */}
          <Button fullWidth mt="lg" onClick={() => navigate(jobsPath)}>
            Acknowledge & Return
          </Button>
        </Stack>
      </Card>
    </div>
  );
};
